import React, { useCallback, useEffect, useMemo, useState } from "react";
import "../styles/shared-ui.css";
import "../styles/substitution-analyst.css";
import SearchableDropdown from "../components/ui/SearchableDropdown";
import { getProcessesPaginated } from "../services/processService";
import { getValueAnalysis } from "../services/valueAnalysisService";
import { getJobHierarchyFlat } from "../services/jobHierarchyService";
import {
  runSubstitutionTrace,
  saveSubstitutionDraft,
  confirmSubstitutionDraft,
} from "../services/substitutionAnalystService";
import { toast } from "../utils/toast";

const RACI_MODES = [
  { value: "R", label: "R only", note: "Only the Responsible party's time is counted as freed — the conservative default." },
  { value: "RA", label: "R + A", note: "Also counts the Accountable party's oversight time. Defensible only if approval is automated too." },
  { value: "ALL", label: "All roles", note: "Counts Consulted and Informed time as well. Most likely to overstate the saving." },
];

function fmtHours(n) {
  const v = Number(n || 0);
  return `${v.toFixed(1)} h`;
}

function fmtPct(n) {
  const v = Number(n || 0);
  return `${v.toFixed(0)}%`;
}

function fmtFte(n) {
  return Number(n || 0).toFixed(2);
}

function severityFor(cascadeRow) {
  if (!cascadeRow) return "none";
  const before = Number(cascadeRow.before?.weekly_hours || 0);
  const after = Number(cascadeRow.after?.weekly_hours || 0);
  const share = before > 0 ? (before - after) / before : 0;
  if (share >= 0.6) return "critical";
  if (share >= 0.25) return "moderate";
  if (share > 0) return "light";
  return "none";
}

function buildImpactedOrgLevels(orgJobs, impactedJobIds) {
  const byId = new Map(orgJobs.map((j) => [j.job_id, j]));
  const keep = new Set();
  impactedJobIds.forEach((id) => {
    let current = byId.get(id);
    let hops = 0;
    while (current && hops < 100) {
      if (keep.has(current.job_id)) break; // already walked this ancestor chain
      keep.add(current.job_id);
      if (current.parent_job_id == null) break;
      current = byId.get(current.parent_job_id);
      hops += 1;
    }
  });

  const depthCache = new Map();
  const depthOf = (id) => {
    if (depthCache.has(id)) return depthCache.get(id);
    depthCache.set(id, 0); // cycle guard
    const job = byId.get(id);
    const parentId = job?.parent_job_id;
    const depth = parentId != null && keep.has(parentId) ? depthOf(parentId) + 1 : 0;
    depthCache.set(id, depth);
    return depth;
  };

  const levels = [];
  keep.forEach((id) => {
    const depth = depthOf(id);
    if (!levels[depth]) levels[depth] = [];
    levels[depth].push(byId.get(id));
  });
  levels.forEach((level) => level.sort((a, b) => (a.name || "").localeCompare(b.name || "")));

  return levels.filter(Boolean);
}

function SubstitutionAnalystPage() {
  // Step 1: process
  const [processes, setProcesses] = useState([]);
  const [processQuery, setProcessQuery] = useState("");
  const [selectedProcess, setSelectedProcess] = useState(null);
  const [processesLoading, setProcessesLoading] = useState(true);

  // Step 2: tasks for the selected process
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [tasksError, setTasksError] = useState(null);
  const [selectedTaskIds, setSelectedTaskIds] = useState(new Set());

  // Run controls
  const [raciScope, setRaciScope] = useState("R");

  // Run state
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState(null);
  const [result, setResult] = useState(null);

  // Save (phase 3)
  const [savedDraftId, setSavedDraftId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);

  // Org chart impact overlay
  const [orgJobs, setOrgJobs] = useState([]);
  const [orgLoading, setOrgLoading] = useState(false);
  const [orgError, setOrgError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: list } = await getProcessesPaginated({ limit: 500 });
        if (!cancelled) setProcesses(Array.isArray(list) ? list : []);
      } catch (e) {
        if (!cancelled) toast.error(e?.message || "Failed to load processes");
      } finally {
        if (!cancelled) setProcessesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const processOptions = useMemo(
    () =>
      processes.map((p) => ({
        label: `${p.process_name}${p.process_code ? ` (${p.process_code})` : ""}`,
        value: p.process_id,
        process: p,
      })),
    [processes],
  );

  const loadTasksForProcess = useCallback(async (processId) => {
    setTasksLoading(true);
    setTasksError(null);
    setTasks([]);
    setSelectedTaskIds(new Set());
    setResult(null);
    setSavedDraftId(null);
    setOrgJobs([]);
    setOrgError(null);
    try {
      const analysis = await getValueAnalysis(processId);
      const rows = (analysis?.tasks || []).filter((t) => !t.is_child_process && t.task_id);
      setTasks(rows);
      // Pre-select the NVA rows — they're the automation candidates this feature exists for.
      const nvaIds = rows.filter((t) => t.value_classification === "NVA").map((t) => t.task_id);
      setSelectedTaskIds(new Set(nvaIds));
    } catch (e) {
      setTasksError(e?.message || "Failed to load this process's tasks");
    } finally {
      setTasksLoading(false);
    }
  }, []);

  const handlePickProcess = (_text, option) => {
    if (!option) return;
    setSelectedProcess(option.process);
    setProcessQuery(option.label);
    loadTasksForProcess(option.process.process_id);
  };

  const toggleTask = (taskId) => {
    setSelectedTaskIds((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  };

  const selectedTaskIdList = useMemo(() => Array.from(selectedTaskIds), [selectedTaskIds]);

  const handleRun = async () => {
    if (!selectedProcess || selectedTaskIdList.length === 0) return;
    setRunning(true);
    setRunError(null);
    setResult(null);
    setSavedDraftId(null);
    try {
      const traceResult = await runSubstitutionTrace(
        selectedProcess.process_id,
        selectedTaskIdList,
        raciScope,
      );
      setResult(traceResult);
    } catch (e) {
      setRunError(e?.message || "The trace failed to run");
    } finally {
      setRunning(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!selectedProcess || !result) return;
    setSaving(true);
    try {
      const draft = await saveSubstitutionDraft(selectedProcess.process_id, {
        taskIds: selectedTaskIdList,
        raciScope,
        result,
      });
      setSavedDraftId(draft?.id ?? null);
      toast.success("Analysis saved as a draft. Nothing was written to any staffing record.");
    } catch (e) {
      toast.error(e?.message || "Failed to save the draft");
    } finally {
      setSaving(false);
    }
  };

  const handleConfirm = async () => {
    if (!selectedProcess || !savedDraftId) return;
    setConfirming(true);
    try {
      await confirmSubstitutionDraft(selectedProcess.process_id, savedDraftId);
      toast.success("Analysis confirmed.");
    } catch (e) {
      toast.error(e?.message || "Failed to confirm the draft");
    } finally {
      setConfirming(false);
    }
  };

  const kpis = result?.kpi_summary || null;
  const cascade = useMemo(() => result?.job_cascade || [], [result]);
  const redeployment = result?.redeployment || [];
  const narrative = result?.narrative || null;

  const impactMap = useMemo(() => new Map(cascade.map((c) => [c.job_id, c])), [cascade]);
  const orgLevels = useMemo(
    () => buildImpactedOrgLevels(orgJobs, cascade.map((c) => c.job_id)),
    [orgJobs, cascade],
  );

  useEffect(() => {
    if (!result || cascade.length === 0 || !selectedProcess?.company_id) return;
    let cancelled = false;
    (async () => {
      setOrgLoading(true);
      setOrgError(null);
      try {
        const jobs = await getJobHierarchyFlat(selectedProcess.company_id);
        if (!cancelled) setOrgJobs(Array.isArray(jobs) ? jobs : []);
      } catch (e) {
        if (!cancelled) setOrgError(e?.message || "Failed to load the org hierarchy");
      } finally {
        if (!cancelled) setOrgLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, selectedProcess?.company_id]);

  return (
    <div className="page-container sa-page">
      <div className="page-header">
        <div className="header-content">
          <div>
            <h2 className="page-title">Substitution Analyst</h2>
            <p className="page-subtitle">
              Trace the RACI → job → FTE impact of automating a set of tasks, before you act on it.
            </p>
          </div>
        </div>
      </div>

      <div className="page-content sa-content">
        {/* Step 1 — process */}
        <section className="sa-card">
          <h3 className="sa-card-title">1. Pick a process</h3>
          <p className="sa-card-hint">
            The trace runs against tasks already assigned to jobs on this process's RACI matrix.
          </p>
          <div className="sa-picker-row">
            <SearchableDropdown
              options={processOptions}
              value={processQuery}
              onChange={(text, option) => {
                setProcessQuery(text);
                if (option) handlePickProcess(text, option);
              }}
              disabled={processesLoading}
              placeholder={processesLoading ? "Loading processes…" : "Search by process name or code…"}
            />
            {selectedProcess && (
              <span className="sa-picked-chip">
                {selectedProcess.process_name}
                <button
                  type="button"
                  aria-label="Clear selected process"
                  onClick={() => {
                    setSelectedProcess(null);
                    setProcessQuery("");
                    setTasks([]);
                    setSelectedTaskIds(new Set());
                    setResult(null);
                    setOrgJobs([]);
                    setOrgError(null);
                  }}
                  style={{ background: "none", border: "none", cursor: "pointer", color: "inherit", fontWeight: 700 }}
                >
                  ×
                </button>
              </span>
            )}
          </div>
        </section>

        {/* Step 2 — tasks */}
        {selectedProcess && (
          <section className="sa-card">
            <h3 className="sa-card-title">2. Select automation candidates</h3>
            <p className="sa-card-hint">
              Tasks classified NVA (Value-Added Analysis) are pre-selected — they're the reason this
              feature exists — but any task on this process can be traced.
            </p>
            {tasksLoading ? (
              <div className="sa-empty">Loading tasks…</div>
            ) : tasksError ? (
              <div className="sa-error-banner">{tasksError}</div>
            ) : tasks.length === 0 ? (
              <div className="sa-empty">This process has no tasks to trace.</div>
            ) : (
              <table className="sa-task-table">
                <thead>
                  <tr>
                    <th style={{ width: 1 }}></th>
                    <th>Task</th>
                    <th>Classification</th>
                  </tr>
                </thead>
                <tbody>
                  {tasks.map((t) => (
                    <tr key={t.task_id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedTaskIds.has(t.task_id)}
                          onChange={() => toggleTask(t.task_id)}
                          aria-label={`Select ${t.task_name}`}
                        />
                      </td>
                      <td>
                        <div className="sa-task-name">{t.task_name}</div>
                        {t.task_code && <div className="sa-task-code">{t.task_code}</div>}
                      </td>
                      <td>
                        {t.value_classification ? (
                          <span className={`sa-pill ${t.value_classification === "NVA" ? "nva" : ""}`}>
                            {t.value_classification}
                          </span>
                        ) : (
                          <span className="sa-pill">Unclassified</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        )}

        {/* Run controls */}
        {selectedProcess && tasks.length > 0 && (
          <section className="sa-card">
            <h3 className="sa-card-title">3. Which RACI roles free up capacity?</h3>
            <div className="sa-run-row">
              <div>
                <div className="sa-raci-seg" role="group" aria-label="RACI scope">
                  {RACI_MODES.map((m) => (
                    <button
                      key={m.value}
                      type="button"
                      aria-pressed={raciScope === m.value}
                      onClick={() => setRaciScope(m.value)}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
                <p className="sa-raci-note">
                  {RACI_MODES.find((m) => m.value === raciScope)?.note}
                </p>
              </div>
              <button
                type="button"
                className="primary-btn"
                disabled={selectedTaskIdList.length === 0 || running}
                onClick={handleRun}
              >
                {running ? "Running…" : `Run trace (${selectedTaskIdList.length} task${selectedTaskIdList.length === 1 ? "" : "s"})`}
              </button>
            </div>
            {running && (
              <div className="sa-run-status" style={{ marginTop: 14 }}>
                <span className="sa-spinner" aria-hidden="true" />
                Resolving RACI assignments and recomputing job workload…
              </div>
            )}
            {runError && (
              <div className="sa-error-banner" style={{ marginTop: 14 }}>{runError}</div>
            )}
          </section>
        )}

        {/* Results */}
        {result && (
          <>
            <section className="sa-card">
              <h3 className="sa-card-title">Organisation-wide impact</h3>
              <div className="sa-kpis">
                <div className="sa-kpi lead">
                  <div className="sa-kpi-label">FTE-equivalent freed</div>
                  <div className="sa-kpi-val">{fmtFte(kpis?.total_fte_freed)}</div>
                </div>
                <div className="sa-kpi">
                  <div className="sa-kpi-label">Jobs impacted</div>
                  <div className="sa-kpi-val">{kpis?.jobs_impacted ?? 0}</div>
                </div>
                <div className="sa-kpi">
                  <div className="sa-kpi-label">Tasks traced</div>
                  <div className="sa-kpi-val">{kpis?.tasks_traced ?? selectedTaskIdList.length}</div>
                </div>
                <div className="sa-kpi">
                  <div className="sa-kpi-label">Redeployment candidates</div>
                  <div className="sa-kpi-val">{kpis?.redeployment_candidates_found ?? 0}</div>
                </div>
              </div>
            </section>

            <section className="sa-card">
              <h3 className="sa-card-title">The cascade, job by job</h3>
              <p className="sa-card-hint">
                Hours and utilisation before and after removing the selected tasks' time from each job,
                under the RACI scope chosen above.
              </p>
              {cascade.length === 0 ? (
                <div className="sa-empty">No job holds any of the selected tasks under this RACI scope.</div>
              ) : (
                <div className="sa-tbl-scroll">
                  <table className="sa-tbl">
                    <thead>
                      <tr>
                        <th>Job</th>
                        <th className="sa-num">Hours freed / week</th>
                        <th>Utilisation</th>
                        <th className="sa-num">FTE freed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cascade.map((c) => (
                        <tr key={c.job_id}>
                          <td>
                            <div className="sa-job-name">{c.job_name}</div>
                            <div className="sa-job-meta">JOB-{c.job_id}</div>
                          </td>
                          <td className="sa-num">{fmtHours(c.before?.weekly_hours - c.after?.weekly_hours)}</td>
                          <td>
                            <div className="sa-util">
                              <span className="sa-util-track">
                                <i className="sa-util-before" style={{ width: `${Math.min(100, c.before?.utilisation_pct || 0)}%` }} />
                                <i className="sa-util-after" style={{ width: `${Math.min(100, c.after?.utilisation_pct || 0)}%` }} />
                              </span>
                              <span className="sa-util-txt">
                                {fmtPct(c.before?.utilisation_pct)} → {fmtPct(c.after?.utilisation_pct)}
                              </span>
                            </div>
                          </td>
                          <td className="sa-num">{fmtFte(c.fte_delta)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {redeployment.length > 0 && (
              <section className="sa-card">
                <h3 className="sa-card-title">Where the freed capacity could go</h3>
                <p className="sa-card-hint">
                  Understaffed jobs ranked by skill overlap with the freed profile.
                </p>
                <div className="sa-tbl-scroll">
                  <table className="sa-tbl">
                    <thead>
                      <tr>
                        <th>Freed from</th>
                        <th className="sa-num">Freed hours</th>
                        <th>Candidate job</th>
                        <th>Skill match</th>
                      </tr>
                    </thead>
                    <tbody>
                      {redeployment.map((r, i) => (
                        <tr key={`${r.source_job_id}-${i}`}>
                          <td>
                            <div className="sa-job-name">{r.source_job_name}</div>
                            <div className="sa-job-meta">JOB-{r.source_job_id}</div>
                          </td>
                          <td className="sa-num">{fmtHours(r.freed_hours)}</td>
                          <td colSpan={2}>
                            {(r.candidates || []).length === 0 ? (
                              <span className="sa-card-hint" style={{ margin: 0 }}>No qualifying candidates found.</span>
                            ) : (
                              r.candidates.map((c) => (
                                <div key={c.job_id} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                                  <span style={{ minWidth: 140 }}>{c.job_name}</span>
                                  <span className="sa-match-bar"><i style={{ width: `${Math.round((c.skill_match_percentage || 0))}%` }} /></span>
                                  <span className="sa-util-txt">{fmtPct(c.skill_match_percentage)}</span>
                                </div>
                              ))
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {cascade.length > 0 && (
              <section className="sa-card">
                <h3 className="sa-card-title">Impact on the org chart</h3>
                <p className="sa-card-hint">
                  Impacted jobs traced up through their reporting line, so the cascade is visible in
                  context — not the full company hierarchy. Border colour tracks how much of that
                  job's week the freed tasks accounted for.
                </p>
                {orgLoading ? (
                  <div className="sa-empty">Loading org hierarchy…</div>
                ) : orgError ? (
                  <div className="sa-error-banner">{orgError}</div>
                ) : orgLevels.length === 0 ? (
                  <div className="sa-empty">No hierarchy data available for these jobs.</div>
                ) : (
                  <>
                    <div className="sa-org-legend">
                      <span><i className="sa-org-swatch critical" /> Redundancy risk</span>
                      <span><i className="sa-org-swatch moderate" /> Material change</span>
                      <span><i className="sa-org-swatch light" /> Minor relief</span>
                      <span><i className="sa-org-swatch none" /> Untouched (ancestor)</span>
                    </div>
                    <div className="sa-org-levels">
                      {orgLevels.map((level, i) => (
                        <React.Fragment key={i}>
                          {i > 0 && <div className="sa-org-connector" />}
                          <div className="sa-org-row">
                            {level.map((job) => {
                              const impact = impactMap.get(job.job_id);
                              const severity = severityFor(impact);
                              return (
                                <div key={job.job_id} className={`sa-org-node ${severity}`}>
                                  <div className="sa-org-node-header">
                                    {job.job_level?.level_name || job.Function?.name || "Job"}
                                  </div>
                                  <div className="sa-org-node-body">
                                    <div className="sa-org-node-name">{job.name}</div>
                                    <div className="sa-org-node-meta">
                                      {impact
                                        ? `−${fmtHours((impact.before?.weekly_hours || 0) - (impact.after?.weekly_hours || 0))} · ${fmtPct(impact.before?.utilisation_pct)} → ${fmtPct(impact.after?.utilisation_pct)}`
                                        : "no impact"}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </React.Fragment>
                      ))}
                    </div>
                  </>
                )}
              </section>
            )}

            {narrative?.is_available && narrative?.text && (
              <section className="sa-card">
                <h3 className="sa-card-title">Summary</h3>
                <p className="sa-card-hint">AI-generated — verify before acting on it.</p>
                <p className="sa-narrative-text">{narrative.text}</p>
              </section>
            )}

            <div className="sa-confirm-bar">
              <p>
                <strong>Nothing here writes to a headcount plan.</strong> Saving keeps this analysis as a
                draft for review; confirming only marks it reviewed — no staffing record changes automatically.
              </p>
              <div style={{ display: "flex", gap: 9 }}>
                <button type="button" className="secondary-btn" disabled={saving || !!savedDraftId} onClick={handleSaveDraft}>
                  {savedDraftId ? "Saved" : saving ? "Saving…" : "Save as draft"}
                </button>
                <button
                  type="button"
                  className="primary-btn"
                  disabled={!savedDraftId || confirming}
                  onClick={handleConfirm}
                >
                  {confirming ? "Confirming…" : "Confirm"}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default SubstitutionAnalystPage;
